import { creatorbase, creatorbaseQueries, Currency } from '@round/api';
import { getTableMetaHelper, TableProps } from '@round/ui-kit';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import useUpdateProjectCampaign from '../../hooks/useUpdateProjectCampaign';
import Skeleton from 'react-loading-skeleton';
import styles from './ProjectCampaignsTable.module.css';
import { DistributiveOmit } from 'utility/utility.types';
import { asMoneyWithoutZeros, numberWithCommas, showNotification } from 'helpers';
import { toDecimalPoint } from '@round/utils';
import { Button, Toggle, assets } from '@round/whitelabel-ui';
import RunDates from 'Modules/Plans/components/RunDates/RunDates';
import moment from 'moment';
import { Dropdown } from '@round/whitelabel-ui';
import { DataState } from 'App.types';
import { tooltipCopy } from 'Modules/Plans/helpers';
import { TooltipHeader } from '@round/whitelabel-ui';
import CircleBadge from 'Modules/Plans/components/CircleBadge/CircleBadge';
import { getUserNameInitials } from 'Modules/Plans/User/user.helpers';
import ExpandOrDragCell from 'Modules/Plans/Campaigns/components/cells/DragCell/ExpandOrDragCell';
import DragAndDropCampaignsTable from 'Modules/Plans/Campaigns/components/CampaignsTable/DragAndDropCampaignsTable';
import CampaignName from 'Modules/Plans/Campaigns/components/CampaignsTable/components/CampaignName/CampaignName';
import ApplicationBadge from '../../../../components/ApplicationBadge/ApplicationBadge';
import AgencyBadge from '../../../../components/AgencyBadge/AgencyBadge';
import AudioPostGrowthCell, {
    Meta as AudioPostGrowthCellMeta,
} from 'Modules/Plans/Campaigns/components/cells/AudioPostGrowthCell';
import AllocationCell, {
    Meta as AllocationCellMeta,
} from 'Modules/Plans/Campaigns/components/cells/AllocationCell/AllocationCell';
import useCurrentUserClients from 'Modules/Plans/Clients/hooks/useCurrentUserClients';
import useProjectDetails from '../../useProjectDetails';
import CampaignCostCell from './CampaignCostCell/CampaignCostCell';
import AddCreatorsCell from './AddCreatorsCell/AddCreatorsCell';

const { Trash: TrashIcon, Plus: PlusIcon, Edit: EditIcon, Copy: CopyIcon, Upload: UploadIcon } = assets;

type AudioTimeSeriesData = DataState<creatorbase.TimeSeriesResponse<'posts_daily_change'>[number]>;

export type AddCreatorsSource = 'explore' | 'pool';

export type AddCreatorsTarget = {
    campaignId: number;
    source: AddCreatorsSource;
};

export type CampaignTableRow = DistributiveOmit<creatorbase.Campaign, 'team_members' | 'currency_id'> & {
    teamMembers: creatorbase.User[];
    stats: creatorbase.AggregatedPostStats | null;
    audioTimeSeries?: AudioTimeSeriesData | null;
    currency: Currency | undefined;
    audioUrl?: string;
    purchaseOrders?: creatorbase.PurchaseOrder[];
};

type Props = Pick<
    TableProps<CampaignTableRow>,
    'data' | 'expandedState' | 'onExpandedChange' | 'renderSubComponent'
> & {
    hasError?: boolean;
    isLoading: boolean;
    statsLoading: number[];
    onEdit?: (campaign: CampaignTableRow) => void;
    onEditBrief?: (campaign: CampaignTableRow) => void;
    onCopyBrief?: (campaign: CampaignTableRow) => void;
    onCopyReport?: (campaign: CampaignTableRow, includeCost: boolean) => void;
    onCopyProposal?: (campaign: CampaignTableRow, includeCost: boolean) => void;
    onCopyAudienceProposal?: (campaign: CampaignTableRow, includeCost: boolean) => void;
    onCreatePost?: (campaign: CampaignTableRow) => void;
    onBulkCreatePost?: (campaign: CampaignTableRow) => void;
    onCreateCampaign: () => void;
    onDeleteCampaign: (campaign: CampaignTableRow) => void;
    onDragEnd?: (newOrder: number[]) => Promise<creatorbase.PatchProjectResponse | undefined>;
    onAddCreatorsToCampaign?: (campaign: CampaignTableRow, source: AddCreatorsSource) => void;
} & Pick<AudioPostGrowthCellMeta<CampaignTableRow>, 'onAudioGrowthCellClick'> &
    Pick<AllocationCellMeta<CampaignTableRow>, 'onAllocate' | 'arePurchaseOrdersLoading'>;

type TableCellContext<T extends keyof CampaignTableRow> = CellContext<CampaignTableRow, CampaignTableRow[T]>;
type Meta = Pick<
    Props,
    | 'isLoading'
    | 'statsLoading'
    | 'onCreatePost'
    | 'onBulkCreatePost'
    | 'onDeleteCampaign'
    | 'onEdit'
    | 'onEditBrief'
    | 'onCopyBrief'
    | 'onCopyReport'
    | 'onCopyProposal'
    | 'onCopyAudienceProposal'
    | 'onAllocate'
    | 'onAddCreatorsToCampaign'
    | 'arePurchaseOrdersLoading'
> &
    Pick<AllocationCellMeta<CampaignTableRow>, 'updateCampaign'> &
    AudioPostGrowthCellMeta<CampaignTableRow>;
const getTableMeta = getTableMetaHelper<Meta>();

const ProjectCampaignsTable = ({
    data,
    isLoading,
    statsLoading,
    hasError,
    onEdit,
    onEditBrief,
    onCopyBrief,
    onCopyReport,
    onCopyProposal,
    onCopyAudienceProposal,
    onCreateCampaign,
    expandedState,
    onExpandedChange,
    renderSubComponent,
    onCreatePost,
    onBulkCreatePost,
    onDeleteCampaign,
    onDragEnd,
    onAudioGrowthCellClick,
    onAllocate,
    onAddCreatorsToCampaign,
    arePurchaseOrdersLoading,
}: Props) => {
    const { team } = useProjectDetails();
    const { updateAsync: updateCampaignAsync } = useUpdateProjectCampaign();
    const updateCampaign = useCallback<typeof creatorbase.patchCampaign>(
        async (id, data) => {
            try {
                const campaign = await updateCampaignAsync({ id, ...data });
                return { status: 200, data: campaign };
            } catch (error) {
                if (
                    error instanceof Error &&
                    creatorbaseQueries.campaigns.utils.update.isExpectedErrorResponse(error)
                ) {
                    return error.response;
                }

                throw error;
            }
        },
        [updateCampaignAsync]
    );

    const columns = useMemo<ColumnDef<CampaignTableRow, any>[]>(
        () => [
            {
                header: '',
                id: 'expand',
                cell: ExpandOrDragCell,
            },
            {
                header: 'Campaign',
                accessorKey: 'name',
                meta: {
                    className: styles.campaignColumn,
                },
                cell: ({ getValue, row: { original }, table }: TableCellContext<'name'>) => {
                    const { isLoading, onEdit, onEditBrief, onCreatePost, updateCampaign } = getTableMeta(table);
                    if (isLoading) {
                        return <Skeleton />;
                    }

                    const isExternallyManagedCampaign = original.managed_by !== 'creatorbase';

                    const campaignName = getValue();
                    return (
                        <div className={styles.campaignContainer}>
                            <CampaignName
                                key={campaignName}
                                campaignId={original.id}
                                name={campaignName}
                                updateName={(name) => updateCampaign?.(original.id, { name })}
                            />

                            <div className={styles.subCampaignNameContainer}>
                                {typeof original.run_by_partnership_id === 'number' ? (
                                    <AgencyBadge
                                        className={styles.applicationBadge}
                                        partnershipId={original.run_by_partnership_id}
                                    />
                                ) : (
                                    original.managed_by !== 'creatorbase' && (
                                        <ApplicationBadge
                                            className={styles.applicationBadge}
                                            application={original.managed_by}
                                        />
                                    )
                                )}

                                <div className={styles.campaignActionsContainer}>
                                    <button
                                        className={styles.campaignActionButton}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit?.(original);
                                        }}
                                    >
                                        Edit campaign
                                    </button>
                                    {!isExternallyManagedCampaign && (
                                        <>
                                            <span>•</span>
                                            <button
                                                className={styles.campaignActionButton}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onEditBrief?.(original);
                                                }}
                                            >
                                                Edit brief
                                            </button>
                                        </>
                                    )}

                                    {!isExternallyManagedCampaign && (
                                        <>
                                            <span>•</span>
                                            <button
                                                className={styles.campaignActionButton}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onCreatePost?.(original);
                                                }}
                                            >
                                                Create post
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                },
            },
            {
                header: 'Posts',
                accessorKey: 'stats',
                id: 'posts',
                cell: ({ getValue, row: { original }, table }: TableCellContext<'stats'>) => {
                    const { isLoading, statsLoading } = getTableMeta(table);
                    if (isLoading || statsLoading.includes(original.id)) {
                        return <Skeleton />;
                    }

                    const stats = getValue();
                    return stats?.post_count ?? '-';
                },
            },
            {
                header: 'Cost',
                id: 'spend',
                cell: CampaignCostCell,
            },
            {
                header: 'Views',
                accessorKey: 'stats',
                id: 'views',
                cell: ({ getValue, row: { original }, table }: TableCellContext<'stats'>) => {
                    const { isLoading, statsLoading } = getTableMeta(table);
                    if (isLoading || statsLoading.includes(original.id)) {
                        return <Skeleton />;
                    }

                    const stats = getValue();
                    return stats?.view_count ? numberWithCommas(stats.view_count) : '-';
                },
            },
            {
                header: () => <TooltipHeader header="CPM" tooltipBody={tooltipCopy.cpm} />,
                accessorKey: 'stats',
                id: 'cpm',
                cell: ({ getValue, row: { original }, table }: TableCellContext<'stats'>) => {
                    const { isLoading, statsLoading } = getTableMeta(table);
                    if (isLoading || statsLoading.includes(original.id)) {
                        return <Skeleton />;
                    }

                    const stats = getValue();

                    const cpmCurrencyValuePair = Object.entries(stats?.cpm ?? {})[0] ?? [];
                    const amount = cpmCurrencyValuePair[1];

                    return asMoneyWithoutZeros(amount, original.currency);
                },
            },
            {
                header: () => (
                    <TooltipHeader
                        header="ER"
                        tooltipTitle="Engagement Rate"
                        tooltipBody={tooltipCopy.engagementRate}
                    />
                ),
                accessorKey: 'stats',
                id: 'er',
                cell: ({ getValue, row: { original }, table }: TableCellContext<'stats'>) => {
                    const { isLoading, statsLoading } = getTableMeta(table);
                    if (isLoading || statsLoading.includes(original.id)) {
                        return <Skeleton />;
                    }

                    const stats = getValue();
                    return typeof stats?.engagement_rate === 'number'
                        ? `${toDecimalPoint(stats?.engagement_rate * 100, 2)}%`
                        : '-';
                },
            },
            {
                header: 'Run dates',
                id: 'runDates',
                cell: ({ row: { original }, table }) => {
                    const { isLoading, statsLoading } = getTableMeta(table);
                    if (isLoading || statsLoading.includes(original.id)) {
                        return <Skeleton />;
                    }

                    if (!original.stats?.first_post_date && !original.stats?.last_post_date) {
                        return '-';
                    }

                    const startDate = moment(original.stats?.first_post_date).format('DD MMM');
                    const endDate = moment(original.stats?.last_post_date).format('DD MMM');

                    return <RunDates startDate={startDate} endDate={endDate} />;
                },
            },
            {
                header: 'Planners',
                accessorKey: 'teamMembers',
                cell: ({ getValue, table }: TableCellContext<'teamMembers'>) => {
                    const { isLoading } = getTableMeta(table);
                    if (isLoading) {
                        return <Skeleton />;
                    }

                    const users = getValue();
                    return (
                        <div className={styles.teamContainer}>
                            {users.map((user, index) => (
                                <CircleBadge key={index} tooltip={user.name}>
                                    {getUserNameInitials(user.name)}
                                </CircleBadge>
                            ))}
                        </div>
                    );
                },
            },
            {
                header: 'PO Allocation',
                id: 'poAllocation',
                cell: AllocationCell,
            },
            {
                header: 'Audio',
                id: 'audioGraph',
                accessorFn: (row) => ({
                    timeSeries: row.audioTimeSeries?.data ?? null,
                    audioUrl: row.audioUrl,
                }),
                meta: { className: styles.dailyChange },
                cell: AudioPostGrowthCell,
            },
            {
                header: undefined,
                id: 'addToCampaign',
                meta: { className: styles.addToCampaignCell },
                cell: AddCreatorsCell,
            },
            {
                header: '',
                id: 'actions',
                meta: {
                    className: styles.actionsCell,
                },
                cell: function Actions({ row: { original }, table }) {
                    const {
                        isLoading,
                        onDeleteCampaign,
                        onCreatePost,
                        onBulkCreatePost,
                        onEdit,
                        onEditBrief,
                        onCopyBrief,
                        onCopyReport,
                        onCopyProposal,
                        onCopyAudienceProposal,
                    } = getTableMeta(table);
                    const [includeCost, setIncludeCost] = useState(true);
                    const [includeReportCost, setIncludeReportCost] = useState(true);

                    if (isLoading) {
                        return;
                    }

                    const isExternallyManagedCampaign = original.managed_by !== 'creatorbase';

                    return (
                        <Dropdown>
                            {({ setIsOpen }) => (
                                <>
                                    <Dropdown.Target />
                                    <Dropdown.Menu>
                                        {!isExternallyManagedCampaign && (
                                            <Dropdown.Item
                                                icon={PlusIcon}
                                                onClick={() => {
                                                    onCreatePost?.(original);
                                                    setIsOpen(false);
                                                }}
                                            >
                                                New post
                                            </Dropdown.Item>
                                        )}

                                        {!isExternallyManagedCampaign && (
                                            <Dropdown.Item
                                                icon={() => <UploadIcon className={styles.bulkUploadIcon} />}
                                                onClick={() => {
                                                    onBulkCreatePost?.(original);
                                                    setIsOpen(false);
                                                }}
                                            >
                                                Upload post URLs
                                            </Dropdown.Item>
                                        )}

                                        <Dropdown.Item
                                            icon={EditIcon}
                                            onClick={() => {
                                                onEdit?.(original);
                                                setIsOpen(false);
                                            }}
                                        >
                                            Edit campaign
                                        </Dropdown.Item>

                                        {!isExternallyManagedCampaign && (
                                            <Dropdown.Item
                                                icon={EditIcon}
                                                onClick={() => {
                                                    onEditBrief?.(original);
                                                    setIsOpen(false);
                                                }}
                                            >
                                                Edit brief
                                            </Dropdown.Item>
                                        )}

                                        {isExternallyManagedCampaign && onCopyReport ? (
                                            <>
                                                <Dropdown.Separator />

                                                <span className={styles.copyMenuTitle}>Copy</span>

                                                <div className={styles.proposalRow}>
                                                    <Dropdown.Item
                                                        icon={() => <CopyIcon className={styles.copyIcon} />}
                                                        style={{ width: 'auto', flex: 1, minWidth: 0 }}
                                                        onClick={() => {
                                                            onCopyReport(original, includeReportCost);
                                                            setIsOpen(false);
                                                        }}
                                                    >
                                                        Report
                                                    </Dropdown.Item>
                                                    <div className={styles.includeCostInline}>
                                                        <span className={styles.includeCostLabel}>Costs</span>
                                                        <Toggle
                                                            value={includeReportCost}
                                                            onChange={setIncludeReportCost}
                                                            ariaLabel="Include costs in report"
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <Dropdown.Separator />

                                                <span className={styles.copyMenuTitle}>Copy</span>

                                                {onCopyProposal && (
                                                    <div className={styles.proposalRow}>
                                                        <Dropdown.Item
                                                            icon={() => <CopyIcon className={styles.copyIcon} />}
                                                            style={{ width: 'auto', flex: 1, minWidth: 0 }}
                                                            onClick={() => {
                                                                onCopyProposal(original, includeCost);
                                                                setIsOpen(false);
                                                            }}
                                                        >
                                                            Proposal
                                                        </Dropdown.Item>
                                                        <div className={styles.includeCostInline}>
                                                            <span className={styles.includeCostLabel}>Costs</span>
                                                            <Toggle
                                                                value={includeCost}
                                                                onChange={setIncludeCost}
                                                                ariaLabel="Include costs"
                                                            />
                                                        </div>
                                                    </div>
                                                )}

                                                {onCopyAudienceProposal && (
                                                    <Dropdown.Item
                                                        icon={() => <CopyIcon className={styles.copyIcon} />}
                                                        onClick={() => {
                                                            onCopyAudienceProposal(original, includeCost);
                                                            setIsOpen(false);
                                                        }}
                                                    >
                                                        Proposal + audience
                                                    </Dropdown.Item>
                                                )}

                                                {onCopyBrief && (
                                                    <Dropdown.Item
                                                        icon={() => <CopyIcon className={styles.copyIcon} />}
                                                        onClick={() => {
                                                            onCopyBrief(original);
                                                            setIsOpen(false);
                                                        }}
                                                    >
                                                        Brief
                                                    </Dropdown.Item>
                                                )}

                                                {onCopyReport && (
                                                    <div className={styles.proposalRow}>
                                                        <Dropdown.Item
                                                            icon={() => <CopyIcon className={styles.copyIcon} />}
                                                            style={{ width: 'auto', flex: 1, minWidth: 0 }}
                                                            onClick={() => {
                                                                onCopyReport(original, includeReportCost);
                                                                setIsOpen(false);
                                                            }}
                                                        >
                                                            Report
                                                        </Dropdown.Item>
                                                        <div className={styles.includeCostInline}>
                                                            <span className={styles.includeCostLabel}>Costs</span>
                                                            <Toggle
                                                                value={includeReportCost}
                                                                onChange={setIncludeReportCost}
                                                                ariaLabel="Include costs in report"
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {!isExternallyManagedCampaign && (
                                            <>
                                                <Dropdown.Separator />

                                                <Dropdown.Item
                                                    icon={() => <TrashIcon className={styles.deleteCampaign} />}
                                                    onClick={() => {
                                                        onDeleteCampaign(original);
                                                        setIsOpen(false);
                                                    }}
                                                    className={styles.deleteCampaign}
                                                >
                                                    Delete campaign
                                                </Dropdown.Item>
                                            </>
                                        )}
                                    </Dropdown.Menu>
                                </>
                            )}
                        </Dropdown>
                    );
                },
            },
        ],
        []
    );

    const meta: Meta = {
        isLoading,
        statsLoading,
        onDeleteCampaign,
        onCreatePost,
        onBulkCreatePost,
        onEdit,
        onEditBrief,
        onCopyBrief,
        onCopyReport,
        onCopyProposal,
        onCopyAudienceProposal,
        getIsTimeSeriesLoading: (row) => {
            const timeSeriesData = row.audioTimeSeries;
            return !!timeSeriesData && timeSeriesData.status === 'loading';
        },
        getTimeSeriesHasError: (row) => {
            const timeSeriesData = row.audioTimeSeries;
            return !!timeSeriesData && timeSeriesData.status === 'error';
        },
        onAudioGrowthCellClick,
        onAllocate,
        onAddCreatorsToCampaign,
        updateCampaign,
        arePurchaseOrdersLoading,
    };

    const { clients } = useCurrentUserClients();
    const isPaymentEnabled = !!clients.find((c) => c.id === team?.client_id)?.is_payment_enabled;

    return (
        <DragAndDropCampaignsTable
            className={styles.table}
            meta={meta}
            data={data}
            columns={columns}
            expandedState={expandedState}
            onExpandedChange={onExpandedChange}
            onRowClick={(row) => {
                if (typeof expandedState === 'object') {
                    onExpandedChange?.({ ...expandedState, [row.id]: !expandedState[row.id] });
                }
            }}
            renderSubComponent={renderSubComponent}
            isLoading={isLoading}
            noDataLabel={
                <div className={styles.noDataContainer} aria-label="no campaigns">
                    {hasError ? (
                        'Could not load campaigns'
                    ) : (
                        <Button
                            appearance="primary"
                            onClick={onCreateCampaign}
                            iconLeft={<PlusIcon className={styles.plusIcon} />}
                        >
                            Campaign
                        </Button>
                    )}
                </div>
            }
            columnVisibility={{
                audioGraph: !!data.find((row) => !!row.audioTimeSeries),
                poAllocation: isPaymentEnabled,
                addToCampaign: !!onAddCreatorsToCampaign,
            }}
            droppableId="project_campaigns"
            onDragEnd={async (result) => {
                const errorMessage = 'Could not reorder campaigns';

                try {
                    const currentOrder = data.map((row) => row.id);
                    const { source, destination } = result;
                    if (!destination) {
                        return;
                    }

                    const sourceIndex = source.index;
                    const destinationIndex = destination.index;

                    const newOrder = [...currentOrder];
                    const [removed] = newOrder.splice(sourceIndex, 1);
                    newOrder.splice(destinationIndex, 0, removed);

                    const response = await onDragEnd?.(newOrder);

                    if (response && response.status !== 200) {
                        showNotification(errorMessage, 'error');
                        return;
                    }
                } catch (e) {
                    if (e instanceof Error && e.name === 'AbortError') {
                        return;
                    }

                    showNotification(errorMessage, 'error');
                }
            }}
        />
    );
};

export default ProjectCampaignsTable;
